<template>
  <v-menu
    offset-y
    min-width="536px"
    max-width="536px"
    nudge-left="398px"
    nudge-bottom="4px"
    v-model="menuOpen"
  >
    <template v-slot:activator="{ on, attrs }">
      <v-btn
        class="otherThreadsButton"
        v-bind="attrs"
        v-on="on"
        text
        :retain-focus-on-click="false"
      >
        {{ $tc('extension.summary.threads', nbOtherAnalysisThreads, { count: nbOtherAnalysisThreads }) }}
        <v-icon>expand_more</v-icon>
      </v-btn>
    </template>
    <div class="analysisSelectorMenuContent d-flex flex-column">
      <div class="d-flex justify-center">
        <v-btn
          class="my-2"
          style="text-transform: none;"
          color="primary"
          text
          @click="analysisSelected({})"
        >
          {{ $t('extension.userAnalyses.newThread') }}
        </v-btn>
      </div>
      <v-divider />
      <ScrollBar max-height="275px">
        <div
          v-for="(analysis, idx) in otherAnalyses"
          :key="analysis.id"
          class="mx-2"
        >
          <v-divider v-if="idx !== 0" />
          <AnalysisThreadCard
            class="d-flex px-4 py-4"
            :analysis="analysisDetails(analysis)"
            @click="analysisSelected"
          />
        </div>
      </ScrollBar>
    </div>
  </v-menu>
</template>
<script>
import { mapGetters, mapActions } from 'vuex';
import AnalysisThreadCard from './AnalysisThreadCard';
import ScrollBar from '../scrollbar/ScrollBar';
import { types as analysesCaptureTypes } from '../../store/analysesCapture';
import { types as analysesTypes } from '../../store/analyses';

export default {
  name: 'AnalysisSelector',
  components: { AnalysisThreadCard, ScrollBar },
  props: {
    documentDetails: {
      type: Object,
      required: true,
    },
    currentAnalysisId: {
      type: String,
      required: false,
      default: undefined,
    },
    onPreparedAnalysis: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      firstSelectionDone: false,
      previousAnalysesWithContentUrl: [],
      menuOpen: false,
    };
  },
  computed: {
    ...mapGetters({
      analysesWithContentUrl: analysesCaptureTypes.getters.analysesWithContentUrl,
      analysisCaptureInProgress: analysesCaptureTypes.getters.analysisCaptureInProgress,
      documentAnalyses: analysesTypes.getters.documentAnalyses,
    }),
    sortedAnalyses() {
      const sortedAnalyses = this.analysesWithContentUrl.slice().sort((a, b) => new Date(b.lastEdited) - new Date(a.lastEdited));
      return sortedAnalyses;
    },
    otherAnalyses() {
      if (this.onPreparedAnalysis) return this.sortedAnalyses;

      const otherAnalyses = this.sortedAnalyses.filter(analyse => analyse.id !== this.currentAnalysisId);
      return otherAnalyses;
    },
    nbOtherAnalysisThreads() {
      return this.onPreparedAnalysis ? this.sortedAnalyses.length : this.otherAnalyses.length;
    },
  },
  methods: {
    ...mapActions({
      fetchAnalysesByUrl: analysesCaptureTypes.actions.fetchAnalysesByUrl,
      fetchDocumentAnalyses: analysesTypes.actions.fetchDocumentAnalyses,
    }),
    analysisDetails(analysis) {
      const detailedAnalysis = this.documentAnalyses.find(a => a.id === analysis.id);
      return {
        ...detailedAnalysis,
        lastEdited: analysis.lastEdited,
      };
    },
    fetchAnalyses() {
      this.fetchAnalysesByUrl(this.documentDetails.url);
    },
    findCapturedAnalysis() {
      const previousAnalyses = new Set(this.previousAnalysesWithContentUrl.map(a => a.id));
      const analyses = this.analysesWithContentUrl.filter(analysis =>
        !previousAnalyses.has(analysis.id));

      if (analyses.length === 1) {
        return analyses[0];
      }

      return undefined;
    },
    analysisSelected(analysis) {
      this.$emit('selectAnalysis', analysis);
    },
    handleSingleAnalysis() {
      const analysis = this.sortedAnalyses[0];
      this.$emit('selectAnalysis', analysis);
    },
    handleMultipleAnalyses() {
      let analysis = this.findCapturedAnalysis();
      this.previousAnalysesWithContentUrl = [];

      const url = new URL(window.location.href);
      const urlSearchParams = new URLSearchParams(url.search);
      const params = Object.fromEntries(urlSearchParams.entries());

      if (params.analysisId) {
        analysis = this.analysesWithContentUrl.find(a => a.id === params.analysisId);
        if (analysis) {
          this.$emit('selectAnalysis', analysis);
          return;
        }
      }

      this.$emit('selectAnalysis', this.sortedAnalyses[0]);
    },
    handleNoAnalysis() {
      this.$emit('selectAnalysis', {});
    },
  },
  watch: {
    // currentAnalysisId is used ONLY by the app (FicheInfo), not the extension
    currentAnalysisId(newVal) {
      if (this.onPreparedAnalysis) {
        return;
      }

      const found = this.documentAnalyses.find(a => a.id === newVal);
      if (found === undefined) {
        this.fetchAnalyses();
      }
    },
    async analysisCaptureInProgress() {
      if (!this.analysisCaptureInProgress.value) {
        this.fetchAnalyses();
      }
    },
    documentDetails() {
      this.fetchAnalyses();
    },
    analysesWithContentUrl() {
      if (this.sortedAnalyses.length > this.documentAnalyses.length) {
        const analysesToLoad = this.analysesWithContentUrl.map(analysis => analysis.id);
        this.fetchDocumentAnalyses(analysesToLoad);
      }

      // Coming from the app (FicheInfo) the right analysis is already loaded, nothing else to do
      if (this.currentAnalysisId) return;

      if (this.analysesWithContentUrl.length > 0 && this.firstSelectionDone) {
        return;
      }

      if (this.analysesWithContentUrl.length > 1) {
        this.handleMultipleAnalyses();
      } else if (this.analysesWithContentUrl.length === 1) {
        this.handleSingleAnalysis();
      } else {
        this.handleNoAnalysis();
      }

      this.firstSelectionDone = true;
    },
  },
};
</script>
<style scoped>
.otherThreadsButton {
  font-size: 13px;
  font-weight: 600;
  text-transform: none;
  height: 32px !important;
  color: #0000008C;
  background-color: #F6F7FF !important;
  border-radius: 6px;
}
.analysisSelectorMenuContent {
  background-color: white;
}
</style>
